import React, { useState, useEffect } from "react";
import classes from "./UploadAndPreviewMultipleImages.module.css";
import PreviewImagePicker from "../preview-picker";
import { IoAdd } from "react-icons/io5";
import { toast } from "react-toastify";
import clsx from "clsx";
import { InformationCircle } from "../../constant";

const UploadAndPreviewMultipleImages = ({
  companyImageTypeId = 1,
  id = null,
  value = [null],
  setter,
}) => {
  const [imageArray, setImageArray] = useState(Array.isArray(value) ? value : [null]);
  const [loading, setLoading] = useState(false);

  // Update internal state when value prop changes
  useEffect(() => {
    if (JSON.stringify(value) !== JSON.stringify(imageArray)) {
      setImageArray(Array.isArray(value) ? value : [null]);
    }
  }, [value]);

  // Handle image change at a specific index
  const onImageChange = (index, file) => {
    const newImages = [...imageArray];
    
    // If this is an existing image with companyImageId
    if (newImages[index] && newImages[index].companyImageId) {
      newImages[index] = {
        ...newImages[index],
        companyImage: file,
        courtId: +id,
        companyImageTypeId,
        delete: false,
        updated: true // Mark as updated for parent component to know
      };
    } else {
      // New image being added
      newImages[index] = {
        courtId: +id,
        companyImageTypeId,
        companyImage: file,
        isNew: true // Mark as new for parent component to know
      };
    }

    // Create a new array for state update to ensure React detects the change
    const updatedImages = [...newImages];
    setImageArray(updatedImages);
    
    if (typeof setter === "function") {
      setter(updatedImages);
    }
  };

  // Handle image deletion
  const onDeleteImage = (index) => {
    // Create a fresh copy of the array
    const newImages = [...imageArray];
    
    // If image has an ID, mark it for deletion but keep it in the array
    if (newImages[index] && newImages[index].companyImageId) {
      // Mark existing image for deletion
      newImages[index] = {
        ...newImages[index],
        delete: true,
        markedForDeletion: true
      };
    } else {
      // If no ID, just remove it from the array
      newImages.splice(index, 1);
    }

    // Check if all images are either deleted or marked for deletion
    const allImagesMarkedOrEmpty = newImages.length === 0 || 
      newImages.every(img => !img || img.markedForDeletion);
    
    // Always keep at least one upload slot if all images are gone
    if (allImagesMarkedOrEmpty) {
      // Make sure we add a fresh null value, not a reference
      newImages.push(null);
    }

    // Create a completely new array to ensure state update
    const updatedImages = [...newImages];
    setImageArray(updatedImages);
    
    if (typeof setter === "function") {
      setter(updatedImages);
    }
  };

  // Handle adding a new empty slot for image upload
  const handleAddMoreImage = () => {
    // Get only visible images (not marked for deletion)
    const visibleImages = imageArray.filter(img => !(img?.markedForDeletion));
    
    // Check if there's already an empty slot in the visible images
    if (visibleImages.some(img => img === null)) {
      toast.info("Please fill in the empty image slot first");
      return;
    }

    // Make sure we don't exceed 5 images
    if (visibleImages.length >= 5) {
      toast.info("You can upload maximum 5 images");
      return;
    }

    // Create a new array with a new null slot
    const updatedImages = [...imageArray, null];
    setImageArray(updatedImages);
    
    if (typeof setter === "function") {
      setter(updatedImages);
    }
  };

  // Get visible images (those not marked for deletion)
  const visibleImages = imageArray.filter(img => !(img?.markedForDeletion));

  return (
    <section className={classes.wrapper}>
      <header className={classes.header}>
        <p className={classes.label}>
          Preview Images
          <span className={classes.infoIcon}>
            <InformationCircle
              className={clsx(classes.infoIcon, "custom-target-icon")}
              size={20}
              color="#a4abb9"
            />
          </span>
        </p>
        <AddMoreImageButton 
          onClick={handleAddMoreImage} 
          disabled={loading || visibleImages.length >= 5} 
        />
      </header>
      <div className={classes.imagesWrapper}>
        {visibleImages.map((image, index) => (
          <PreviewImagePicker
            key={`img-${index}-${Date.now()}`}
            value={image?.companyImage}
            onChange={(file) => onImageChange(
              imageArray.findIndex((img, i) => 
                !img?.markedForDeletion && 
                visibleImages.indexOf(img) === index
              ), 
              file
            )}
            onDelete={() => onDeleteImage(
              imageArray.findIndex((img, i) => 
                !img?.markedForDeletion && 
                visibleImages.indexOf(img) === index
              )
            )}
            name={`courtImage-${index}`}
            disabled={loading}
          />
        ))}
      </div>
    </section>
  );
};

const AddMoreImageButton = ({ onClick, disabled }) => {
  return (
    <button
      type="button"
      className={classes.addMoreImageButton}
      onClick={onClick}
      disabled={disabled}
    >
      <IoAdd color="var(--secondary-color)" size={20} />
      <span className={classes.addMoreImageButtonText}>Add More Image</span>
    </button>
  );
};

export default UploadAndPreviewMultipleImages;
